/**
 * OnboardingFlow - Welcome sequence for new players
 * Displays after mode selection, introduces core game concepts
 * Skippable, saves completion flag to database
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { Button } from "@/components/ui/button";
import { useRadio } from "../ui/RadioPlayer.jsx";

// Panel content - streamlined for single mode
const getPanels = (t) => {
  return [
    {
      id: "welcome",
      title: t('onboarding.welcome.title'),
      subtitle: t('onboarding.welcome.subtitle'),
      content: [
        t('onboarding.welcome.line1'),
        "",
        t('onboarding.welcome.line2'),
      ],
      highlight: null,
    },
    {
      id: "portland",
      title: t('onboarding.portland.title'),
      content: [
        t('onboarding.portland.line1'),
        "",
        t('onboarding.portland.line2'),
        "",
        t('onboarding.portland.line3'),
      ],
      highlight: "map",
    },
    {
      id: "slots",
      title: t('onboarding.slots.title'),
      subtitle: t('onboarding.slots.subtitle'),
      content: [
        t('onboarding.slots.line1'),
        "",
        t('onboarding.slots.line2'),
      ],
      highlight: "slots",
    },
    {
      id: "network",
      title: t('onboarding.network.title'),
      subtitle: t('onboarding.network.subtitle'),
      content: [
        t('onboarding.network.line1'),
        "",
        t('onboarding.network.line2'),
      ],
      highlight: "routes",
    },
    {
      id: "reputation",
      title: t('onboarding.reputation.title'),
      subtitle: t('onboarding.reputation.subtitle'),
      content: [
        t('onboarding.reputation.line1'),
        "",
        t('onboarding.reputation.line2'),
      ],
      highlight: "reputation",
    },
    {
      id: "relations",
      title: t('onboarding.relations.title'),
      subtitle: t('onboarding.relations.subtitle'),
      content: [
        t('onboarding.relations.line1'),
        "",
        t('onboarding.relations.line2'),
      ],
      highlight: "relations",
    },
    {
      id: "mode",
      title: t('onboarding.mode.title'),
      subtitle: t('onboarding.mode.subtitle'),
      content: [
        t('onboarding.mode.line1'),
        "",
        t('onboarding.mode.line2'),
        "",
        t('onboarding.mode.line3'),
        "",
        t('onboarding.mode.line4'),
      ],
      highlight: null,
    },
    {
      id: "eos",
      title: t('onboarding.eos.title'),
      subtitle: t('onboarding.eos.subtitle'),
      content: [
        t('onboarding.eos.line1'),
        "",
        t('onboarding.eos.req1'),
        t('onboarding.eos.req2'),
        t('onboarding.eos.req3'),
        t('onboarding.eos.req4'),
        t('onboarding.eos.req5'),
        "",
        t('onboarding.eos.line2'),
        "",
        t('onboarding.eos.line3'),
      ],
      highlight: "eos",
    },
    {
      id: "go",
      title: t('onboarding.go.title'),
      subtitle: t('onboarding.go.subtitle'),
      content: [
        t('onboarding.go.line1'),
        "",
        t('onboarding.go.line2'),
      ],
      highlight: null,
      isFinal: true,
    },
  ];
};

export default function OnboardingFlow({ onComplete, gameMode }) {
  const { t } = useTranslation();
  const { worldState, updateWorldState } = useWorldState();
  const { play: playRadio } = useRadio();
  const [currentPanel, setCurrentPanel] = useState(0);
  const [textVisible, setTextVisible] = useState(false);
  const [titleVisible, setTitleVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const audioRef = useRef(null);
  const [audioLoaded, setAudioLoaded] = useState(false);

  const panels = getPanels(t);
  const panel = panels[currentPanel];

  // Initialize and fade in background music
  useEffect(() => {
    const audio = new Audio("./music/Late Shift Radar - 1.ogg");
    audio.loop = true;
    audio.volume = 0;
    audioRef.current = audio;

    const handleCanPlay = () => {
      setAudioLoaded(true);
      // Start playing and fade in
      audio.play().catch(e => console.warn("Audio autoplay blocked:", e));

      // Fade in over 3 seconds
      let volume = 0;
      const fadeInterval = setInterval(() => {
        volume += 0.02;
        if (volume >= 0.4) {
          volume = 0.4;
          clearInterval(fadeInterval);
        }
        audio.volume = volume;
      }, 60);
    };

    audio.addEventListener("canplaythrough", handleCanPlay);
    audio.load();

    return () => {
      audio.removeEventListener("canplaythrough", handleCanPlay);
      // Fade out on unmount
      if (audioRef.current) {
        const fadeOut = setInterval(() => {
          if (audioRef.current.volume > 0.02) {
            audioRef.current.volume -= 0.02;
          } else {
            clearInterval(fadeOut);
            audioRef.current.pause();
            audioRef.current = null;
          }
        }, 30);
      }
    };
  }, []);

  // Animate text on panel change
  useEffect(() => {
    setTitleVisible(false);
    setTextVisible(false);

    // Stagger the animations
    const titleTimer = setTimeout(() => setTitleVisible(true), 100);
    const textTimer = setTimeout(() => setTextVisible(true), 400);

    return () => {
      clearTimeout(titleTimer);
      clearTimeout(textTimer);
    };
  }, [currentPanel]);

  // Handle continue
  const handleContinue = useCallback(() => {
    if (currentPanel < panels.length - 1) {
      setCurrentPanel(prev => prev + 1);
    } else {
      handleComplete();
    }
  }, [currentPanel, panels.length]);

  // Handle skip/complete
  const handleComplete = useCallback(async () => {
    if (isExiting) return; // Prevent double-trigger

    // Start exit animation
    setIsExiting(true);

    // Save completion flag
    updateWorldState(prev => ({
      ...prev,
      onboardingComplete: true,
    }));

    // Slow fade out music over ~4 seconds, then call onComplete
    if (audioRef.current) {
      const startVolume = audioRef.current.volume;
      const fadeSteps = 80; // 80 steps over 4 seconds = 50ms per step
      let step = 0;

      const fadeOut = setInterval(() => {
        step++;
        if (audioRef.current && step < fadeSteps) {
          // Ease out - faster at start, slower at end
          const progress = step / fadeSteps;
          const eased = 1 - Math.pow(1 - progress, 2);
          audioRef.current.volume = startVolume * (1 - eased);
        } else {
          clearInterval(fadeOut);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
          }
          // Only unmount after music fades
          onComplete?.();
          // Start radio 1 second after onboarding ends
          // Set localStorage directly to ensure it persists even if component unmounts
          localStorage.setItem('airlineOpsRadioEnabled', 'true');
          setTimeout(() => playRadio(), 1000);
        }
      }, 50);
    } else {
      // No audio, just complete after brief delay
      setTimeout(() => {
        onComplete?.();
        // Start radio 1 second after onboarding ends
        localStorage.setItem('airlineOpsRadioEnabled', 'true');
        setTimeout(() => playRadio(), 1000);
      }, 500);
    }
  }, [isExiting, onComplete, updateWorldState, playRadio]);

  // Handle click anywhere to continue
  const handleOverlayClick = useCallback((e) => {
    if (isExiting) return; // Ignore during exit
    // Don't advance if clicking buttons
    if (e.target.closest("button")) return;
    handleContinue();
  }, [handleContinue, isExiting]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isExiting) return; // Ignore input during exit
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowRight") {
        e.preventDefault();
        handleContinue();
      } else if (e.key === "Escape") {
        handleComplete();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleContinue, handleComplete, isExiting]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-700"
      onClick={handleOverlayClick}
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        cursor: isExiting ? "default" : "pointer",
        opacity: isExiting ? 0 : 1,
        pointerEvents: isExiting ? "none" : "auto",
      }}
    >
      {/* Subtle background pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, #60ffd0 1px, transparent 1px)`,
          backgroundSize: "50px 50px",
        }}
      />

      {/* Main content container */}
      <div className="relative max-w-3xl mx-auto px-8 py-12 text-center">
        {/* Progress indicator */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 flex gap-2">
          {panels.map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full transition-all duration-300"
              style={{
                backgroundColor: i === currentPanel ? "#60ffd0" : i < currentPanel ? "#2d5a4a" : "#333",
                transform: i === currentPanel ? "scale(1.3)" : "scale(1)",
              }}
            />
          ))}
        </div>

        {/* Title */}
        <h1
          className="text-4xl md:text-5xl font-bold transition-all duration-500"
          style={{
            color: "#60ffd0",
            opacity: titleVisible ? 1 : 0,
            transform: titleVisible ? "translateY(0)" : "translateY(-20px)",
            textShadow: "0 0 40px rgba(96, 255, 208, 0.3)",
            marginBottom: panel.subtitle ? "0.5rem" : "2rem",
          }}
        >
          {panel.title}
        </h1>

        {/* Subtitle */}
        {panel.subtitle && (
          <p
            className="text-xl md:text-2xl italic mb-8 transition-all duration-500"
            style={{
              color: "#a1a1a1",
              opacity: titleVisible ? 1 : 0,
              transform: titleVisible ? "translateY(0)" : "translateY(-10px)",
            }}
          >
            {panel.subtitle}
          </p>
        )}

        {/* Content */}
        <div
          className="space-y-4 text-lg md:text-xl leading-relaxed transition-all duration-700"
          style={{
            color: "#e5e5e5",
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
          }}
        >
          {panel.content.map((line, i) => (
            <p
              key={i}
              className={line === "" ? "h-4" : ""}
              style={{
                color: line.startsWith("  -") ? "#a1a1a1" : "#e5e5e5",
                fontFamily: line.startsWith("  -") ? "monospace" : "inherit",
              }}
            >
              {line}
            </p>
          ))}
        </div>

        {/* Highlight indicator */}
        {panel.highlight && (
          <div
            className="mt-8 inline-block px-4 py-2 rounded-lg animate-pulse"
            style={{
              backgroundColor: "rgba(96, 255, 208, 0.1)",
              border: "1px solid rgba(96, 255, 208, 0.3)",
              color: "#60ffd0",
              fontSize: "0.875rem",
            }}
          >
            {panel.highlight === "map" && t('onboarding.hints.map')}
            {panel.highlight === "slots" && t('onboarding.hints.slots')}
            {panel.highlight === "reputation" && t('onboarding.hints.reputation')}
            {panel.highlight === "relations" && t('onboarding.hints.relations')}
            {panel.highlight === "routes" && t('onboarding.hints.routes')}
            {panel.highlight === "eos" && t('onboarding.hints.eos')}
          </div>
        )}

        {/* Buttons */}
        <div
          className="mt-12 flex justify-center gap-4 transition-all duration-500"
          style={{
            opacity: textVisible ? 1 : 0,
          }}
        >
          {!panel.isFinal && (
            <Button
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleComplete();
              }}
              className="px-6 py-2 text-base"
              style={{
                color: "#737373",
                borderColor: "#333",
              }}
            >
              {t('onboarding.skip')}
            </Button>
          )}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleContinue();
            }}
            className="px-8 py-2 text-base font-bold"
            style={{
              backgroundColor: "#60ffd0",
              color: "#000",
            }}
          >
            {panel.isFinal ? t('onboarding.begin') : t('onboarding.continue')}
          </Button>
        </div>

        {/* Click hint */}
        <p
          className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs transition-opacity duration-1000"
          style={{
            color: "#525252",
            opacity: textVisible ? 1 : 0,
          }}
        >
          {t('onboarding.clickHint')}
        </p>
      </div>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes subtlePulse {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
        .animate-pulse {
          animation: subtlePulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
